const WISHLIST_ADD_URL = "https://penn.dev/wishlist/add-to-wishlist"; //  Full URL!
const WISHLIST_REMOVE_URL = "https://penn.dev/wishlist/remove-from-wishlist"; // Full URL!
const FAVORITES_ADD_URL = "https://penn.dev/wishlist/add-to-favorites"; // Full URL!
const FAVORITES_REMOVE_URL = "https://penn.dev/wishlist/remove-from-favorites"; // Full URL!
let customerId = getCustomerId();

//  ---  Helper Functions  ---

/**
 * Sends a POST request to add a product to the wishlist.
 *
 * @param {string} shop       - The Shopify shop domain.
 * @param {string} customerId - The customer's unique identifier.
 * @param {string} productId  - The ID of the product to add.
 * @returns {Promise<object>} - The JSON response from the server.
 * @throws  {Error}           - If the request fails.
 */
async function addToWishlist(shop, customerId, productId) {
  try {
    const response = await fetch(WISHLIST_ADD_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shop, customerId, productId }),
    });

    if (!response.ok) {
      //  Handle HTTP errors (e.g., 400, 500)
      let errorText = `HTTP Error: ${response.status} - ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.error) {
          errorText += ` | Server Error: ${JSON.stringify(errorData.error)}`;
        }
      } catch (jsonError) {
        //  If we can't parse JSON, just use the HTTP error
        console.warn("Could not parse error JSON", jsonError);
      }
      throw new Error(errorText);
    }

    return await response.json(); //  Return the parsed JSON data
  } catch (error) {
    console.error("addToWishlist failed:", error);
    throw error; //  Re-throw the error for the caller to handle
  }
}

/**
 * Sends a POST request to remove a product from the wishlist.
 * (Similar structure to addToWishlist)
 */
async function removeFromWishlist(shop, customerId, productId) {
  try {
    const response = await fetch(WISHLIST_REMOVE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shop, customerId, productId }),
    });

    if (!response.ok) {
      let errorText = `HTTP Error: ${response.status} - ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.error) {
          errorText += ` | Server Error: ${JSON.stringify(errorData.error)}`;
        }
      } catch (jsonError) {
        console.warn("Could not parse error JSON", jsonError);
      }
      throw new Error(errorText);
    }

    return await response.json();
  } catch (error) {
    console.error("removeFromWishlist failed:", error);
    throw error;
  }
}

/**
 * Sends a POST request to add a collection to favorites.
 *
 * @param {string} shop        - The Shopify shop domain.
 * @param {string} customerId  - The customer's unique identifier.
 * @param {string} collectionId - The ID of the collection to add.
 * @returns {Promise<object>}  - The JSON response from the server.
 * @throws  {Error}            - If the request fails.
 */
async function addToFavorites(shop, customerId, collectionId) {
  try {
    const response = await fetch(FAVORITES_ADD_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shop, customerId, collectionId }),
    });

    if (!response.ok) {
      let errorText = `HTTP Error: ${response.status} - ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.error) {
          errorText += ` | Server Error: ${JSON.stringify(errorData.error)}`;
        }
      } catch (jsonError) {
        console.warn("Could not parse error JSON", jsonError);
      }
      throw new Error(errorText);
    }

    return await response.json();
  } catch (error) {
    console.error("addToFavorites failed:", error);
    throw error;
  }
}

/**
 * Sends a POST request to remove a collection from favorites.
 */
async function removeFromFavorites(shop, customerId, collectionId) {
  try {
    const response = await fetch(FAVORITES_REMOVE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ shop, customerId, collectionId }),
    });

    if (!response.ok) {
      let errorText = `HTTP Error: ${response.status} - ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData && errorData.error) {
          errorText += ` | Server Error: ${JSON.stringify(errorData.error)}`;
        }
      } catch (jsonError) {
        console.warn("Could not parse error JSON", jsonError);
      }
      throw new Error(errorText);
    }

    return await response.json();
  } catch (error) {
    console.error("removeFromFavorites failed:", error);
    throw error;
  }
}

/**
 * Gets the customer ID.  **YOU MUST IMPLEMENT THIS!**
 *
 * This is a placeholder function.  You need to replace this
 * with the actual logic to retrieve the customer's unique ID
 * from your application's authentication system.
 *
 * Examples:
 * - From a cookie
 * - From local storage (use cautiously for sensitive data)
 * - From a server-side session
 * - From a hidden input field
 * - From your authentication provider's API
 *
 * @returns {string|null} The customer's ID, or null if not available.
 */
function getCustomerId() {
  if (typeof shopifyCustomerId !== "undefined") {
    return shopifyCustomerId.toString();
  }
  return null;
}

//  ---  Event Handling  ---

/**
 * Handles clicks on wishlist and favorites buttons.
 */
async function handleClick(event) {
  const target = event.target.closest('.add-to-wishlist, .remove-from-wishlist, .add-to-favorites, .remove-from-favorites');
  if (!target) {
    // Only log if we clicked on a button or element that might be related to wishlist/favorites
    if (event.target.closest('button') || event.target.closest('[data-shop]')) {
      console.log("No wishlist or favorites button found in click path");
    }
    return;
  }

  // Check if we're on the customer dashboard page
  const isCustomerDashboard = window.location.pathname.includes('/pages/customer-dashboard');

  // Handle Wishlist Buttons
  if (target.classList.contains("add-to-wishlist")) {
    const shop = target.dataset.shop;
    const productId = target.dataset.productId;

    if (!shop || !productId) {
      console.error("Missing data-shop or data-product-id on add button:", target);
      return;
    }

    if (!customerId) {
      console.error("Please log in to use the wishlist.");
      return;
    }

    target.disabled = true;
    target.classList.add("loading");

    try {
      const response = await addToWishlist(shop, customerId, productId);
      console.log("✅ Product successfully added to wishlist!", response);
    } catch (error) {
      console.error("❌ Failed to add to wishlist:", error.message || error);
    } finally {
      target.disabled = false;
      target.classList.remove("loading");
    }

  } else if (target.classList.contains("remove-from-wishlist")) {
    const shop = target.dataset.shop;
    const productId = target.dataset.productId;

    if (!shop || !productId) {
      console.error("Missing data-shop or data-product-id on remove button:", target);
      return;
    }

    if (!customerId) {
      console.error("Please log in to use the wishlist.");
      return;
    }

    target.disabled = true;
    target.classList.add("loading");

    try {
      const response = await removeFromWishlist(shop, customerId, productId);
      console.log("✅ Product successfully removed from wishlist!", response);
      
      // Only hide the product card if we're on the customer dashboard
      if (isCustomerDashboard) {
        const productCard = target.closest('.product-card-wrapper');
        if (productCard) {
          productCard.style.display = 'none';
        }
      }
    } catch (error) {
      console.error("❌ Failed to remove from wishlist:", error.message || error);
    } finally {
      target.disabled = false;
      target.classList.remove("loading");
    }
  }

  // Handle Favorites Buttons
  if (target.classList.contains("add-to-favorites")) {
    const shop = target.dataset.shop;
    const collectionId = target.dataset.collectionId;

    if (!shop || !collectionId) {
      console.error("Missing data-shop or data-collection-id on add button:", target);
      return;
    }

    if (!customerId) {
      console.error("Please log in to use favorites.");
      return;
    }

    target.disabled = true;
    target.classList.add("loading");

    try {
      const response = await addToFavorites(shop, customerId, collectionId);
      console.log("✅ Collection successfully added to favorites!", response);
      
      // Refresh the favorites list with latest metafield data
      await refreshFavoritesList();
    } catch (error) {
      console.error("❌ Failed to add to favorites:", error.message || error);
    } finally {
      target.disabled = false;
      target.classList.remove("loading");
    }

  } else if (target.classList.contains("remove-from-favorites")) {
    const shop = target.dataset.shop;
    const collectionId = target.dataset.collectionId;

    if (!shop || !collectionId) {
      console.error("Missing data-shop or data-collection-id on remove button:", target);
      return;
    }

    if (!customerId) {
      console.error("Please log in to use favorites.");
      return;
    }

    target.disabled = true;
    target.classList.add("loading");

    try {
      const response = await removeFromFavorites(shop, customerId, collectionId);
      console.log("✅ Collection successfully removed from favorites!", response);
      
      // Only hide the collection card if we're on the customer dashboard
      if (isCustomerDashboard) {
        const collectionCard = target.closest('.collection-list__item');
        if (collectionCard) {
          collectionCard.style.display = 'none';
        }
      }
      
      // Refresh the favorites list with latest metafield data
      await refreshFavoritesList();
    } catch (error) {
      console.error("❌ Failed to remove from favorites:", error.message || error);
    } finally {
      target.disabled = false;
      target.classList.remove("loading");
    }
  }
}

//  Attach the event listener
document.addEventListener("click", handleClick);

//  ---  Initialization  ---

//  Get customer ID on page load (if applicable)
window.addEventListener("load", () => {
  customerId = getCustomerId();
});

document.addEventListener("DOMContentLoaded", function () {
  // Handle Wishlist Buttons
  document.querySelectorAll(".add-to-wishlist").forEach((addBtn) => {
    addBtn.addEventListener("click", function () {
      const container = this.closest(".product-card") || this.parentElement;
      const removeBtn = container.querySelector(".remove-from-wishlist");

      this.style.display = "none";
      if (removeBtn) removeBtn.style.display = "inline-block";
    });
  });

  document.querySelectorAll(".remove-from-wishlist").forEach((removeBtn) => {
    removeBtn.addEventListener("click", function () {
      const container = this.closest(".product-card") || this.parentElement;
      const addBtn = container.querySelector(".add-to-wishlist");

      this.style.display = "none";
      if (addBtn) addBtn.style.display = "inline-block";
    });
  });

  // Handle Favorites Buttons
  document.querySelectorAll(".add-to-favorites").forEach((addBtn) => {
    addBtn.addEventListener("click", function () {
      const container = this.closest(".collection-card") || this.parentElement;
      const removeBtn = container.querySelector(".remove-from-favorites");

      this.style.display = "none";
      if (removeBtn) removeBtn.style.display = "inline-block";
    });
  });

  document.querySelectorAll(".remove-from-favorites").forEach((removeBtn) => {
    removeBtn.addEventListener("click", function () {
      const container = this.closest(".collection-card") || this.parentElement;
      const addBtn = container.querySelector(".add-to-favorites");

      this.style.display = "none";
      if (addBtn) addBtn.style.display = "inline-block";
    });
  });
});

/**
 * Fetches the latest favorites data from the customer's metafield and updates the DOM
 */
async function refreshFavoritesList() {
  try {
    // Fetch the current page content to get updated metafield data
    const response = await fetch(window.location.href);
    const html = await response.text();
    
    // Create a temporary DOM element to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    // Update the favorites list
    const newFavoritesWrapper = doc.querySelector('.favorites-list-wrapper');
    if (newFavoritesWrapper) {
      const currentFavoritesWrapper = document.querySelector('.favorites-list-wrapper');
      if (currentFavoritesWrapper) {
        currentFavoritesWrapper.innerHTML = newFavoritesWrapper.innerHTML;
      }
    }
    
    // Reattach event listeners to the new buttons
    attachFavoritesEventListeners();
    
  } catch (error) {
    console.error('Failed to refresh favorites list:', error);
  }
}

/**
 * Attaches event listeners to the favorites buttons
 */
function attachFavoritesEventListeners() {
  // Handle Favorites Buttons
  document.querySelectorAll(".add-to-favorites").forEach((addBtn) => {
    addBtn.addEventListener("click", function () {
      const container = this.closest(".collection-card") || this.parentElement;
      const removeBtn = container.querySelector(".remove-from-favorites");

      this.style.display = "none";
      if (removeBtn) removeBtn.style.display = "inline-block";
    });
  });

  document.querySelectorAll(".remove-from-favorites").forEach((removeBtn) => {
    removeBtn.addEventListener("click", function () {
      const container = this.closest(".collection-card") || this.parentElement;
      const addBtn = container.querySelector(".add-to-favorites");

      this.style.display = "none";
      if (addBtn) addBtn.style.display = "inline-block";
    });
  });
}